import { animate, style, transition, trigger } from '@angular/animations';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import {
	ButtonComponent,
	ButtonState,
	CustomWindow,
	FeatureService,
	isIEOrEdge,
	LocalStorageService,
	ModalOverlayService,
	ShopperService,
	StorageKeys,
	THEME_CONSTANTS,
	WINDOW,
} from '@woolworthsnz/styleguide';
import { ContextResponse, ProductResponse, QuantityResponse } from '@woolworthsnz/trader-api';
import { Subscription } from 'rxjs';
import { AddToTrolleyUpdateRequest } from '../../models/add-to-trolley-update-request.model';
import { ProductService } from '../../services/product.service';
import { ProductState } from '../../services/product.state';
import { PubSubService } from '../../services/pub-sub.service';
import { LayoutType } from '../../models/layout-type.model';
import { getFormattedAvailabilityStatus } from '../../helpers';
import { QuantitySelectorComponent } from '../quantity-selector/quantity-selector.component';
import { NgIf } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'product-add-to-trolley, [productAddToTrolley]',
	templateUrl: './add-to-trolley.component.html',
	styleUrls: ['./add-to-trolley.component.scss'],
	animations: [
		trigger('animation', [
			transition(':enter', [
				style({ opacity: 0 }),
				animate(!isIEOrEdge() ? `${THEME_CONSTANTS.transitions.default} 225ms` : '0ms', style({ opacity: 1 })),
			]),
			transition(':leave', [
				style({ opacity: 1 }),
				animate(!isIEOrEdge() ? `${THEME_CONSTANTS.transitions.default} 150ms` : '0ms', style({ opacity: 0 })),
			]),
		]),
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, QuantitySelectorComponent, ButtonComponent],
})
export class AddToTrolleyComponent implements OnInit, OnDestroy {
	@Input() unit: ProductResponse.UnitEnum;
	@Input() shopper: string;
	@Input() id: number;
	@Input() title: string;
	@Input() layout: LayoutType = LayoutType.grid;
	@Input() averageWeightPerUnit: number | undefined;
	@Input() productIsAvailable = true;
	@Input() productIsInStock = true;

	@Output() addToTrolleyEmitter: EventEmitter<void> = new EventEmitter();
	@Output() updateQuantityEmitter: EventEmitter<number> = new EventEmitter();
	@Output() changeEmitter: EventEmitter<number> = new EventEmitter();

	changeText = 'Change';
	buttonState = ButtonState.default;
	animationCompleted = true;
	quantityResponse: QuantityResponse;
	updateRequest: AddToTrolleyUpdateRequest = {
		id: 0,
		quantity: 0,
	};
	addToTrolleySub: Subscription;
	updateQuantitySub: Subscription;
	availabilityStatus: string;
	productState$ = this.productService.state$.pipe(takeUntilDestroyed());

	constructor(
		private featureService: FeatureService,
		private productService: ProductService,
		private pubsubService: PubSubService,
		private shopperService: ShopperService,
		private modalOverlayService: ModalOverlayService,
		private localStorageService: LocalStorageService,
		private cdr: ChangeDetectorRef,
		@Inject(WINDOW) private window: CustomWindow
	) {
		this.addToTrolleySub = this.pubsubService.On('onAddToTrolleyClick').subscribe((response) => {
			if (response.id !== this.id) {
				return;
			}
			// TODO:  [POD-3276] - remove shopper checks and state updates from here also
			if (this.shopper) {
				this.shopperService.addToTrolley$.next();
				this.buttonState = ButtonState.busy;

				this.productService.setState({
					submitted: true,
				});
			}

			// Forcing the emission to the next tick so that the button can acknowledge it's busy
			setTimeout(() => {
				this.addToTrolleyEmitter.emit();
			}, 1);
		});

		this.updateQuantitySub = this.pubsubService.On('onUpdateQuantityClick').subscribe((response) => {
			if (response.id !== this.id) {
				return;
			}
			this.updateQuantityEmitter.emit(response.quantity);
		});
	}

	get isEachItem(): boolean {
		return this.unit === ProductResponse.UnitEnum.Each;
	}

	get buttonText(): string {
		if (this.productIsInStock) {
			return 'Add to trolley';
		}

		return this.productIsAvailable ? getFormattedAvailabilityStatus(this.availabilityStatus) : 'Unavailable';
	}

	get ariaButtonText(): string {
		if (this.productIsInStock) {
			return `Add ${this.title} to trolley`;
		}

		return this.productIsAvailable
			? `${this.title} is ${getFormattedAvailabilityStatus(this.availabilityStatus)}`
			: `${this.title} is unavailable`;
	}

	get showQuantitySelector(): boolean {
		return this.productIsInStock && (!!this.quantityResponse?.value || (0 > 0 && this.animationCompleted));
	}

	get canAnimate(): boolean {
		return !isIEOrEdge(this.window);
	}

	ngOnInit(): void {
		this.productState$.subscribe((productState: ProductState) => {
			switch (true) {
				case productState.submitted: {
					this.buttonState = ButtonState.busy;
					break;
				}

				case productState.completed: {
					this.buttonState = ButtonState.completed;
					break;
				}

				case productState.error:
				default: {
					this.buttonState = ButtonState.default;
					break;
				}
			}

			this.quantityResponse = productState.product?.quantity || {};

			this.averageWeightPerUnit = (productState.product as ProductResponse)?.averageWeightPerUnit;

			if (productState.product?.availabilityStatus) {
				this.availabilityStatus = productState.product.availabilityStatus;
			}

			const quantityInTrolley = productState.quantityInTrolley;

			if (quantityInTrolley) {
				this.setValue(quantityInTrolley);
			} else {
				this.resetAddToTrolleyButton();
			}

			this.cdr.markForCheck();
		});
	}

	ngOnDestroy(): void {
		this.addToTrolleySub.unsubscribe();
		this.updateQuantitySub.unsubscribe();
	}

	addToTrolleyClick(): void {
		if (this.shouldShowLiquorLegislationModal()) {
			const confirmAction = (): void => {
				const scvid = this.shopperService.state?.shopperScvId || 'confirmed';
				this.localStorageService.setItem(StorageKeys.ageRestrictedProductAcknowledged, scvid);
				this.addToTrolleyClick();
			};
			this.modalOverlayService.openLiquorLegislationModal(confirmAction);
		} else {
			this.pubsubService.Broadcast('onAddToTrolleyClick', this);
		}
	}

	addToTrolleyAnimationComplete(): void {
		this.animationCompleted = true;
	}

	changeQuantityClick(event: number): void {
		this.changeEmitter.emit(event);
	}

	resetAddToTrolleyButton(): void {
		this.animationCompleted = false;
	}

	setValue = (value: number): void => {
		this.quantityResponse = {
			...this.quantityResponse,
			value,
		};
	};

	updateQuantityClick(event: number): void {
		this.updateRequest.id = this.id;
		this.updateRequest.quantity = event;
		this.pubsubService.Broadcast('onUpdateQuantityClick', this.updateRequest);
	}

	shouldShowLiquorLegislationModal(): boolean {
		const IsAgeRestrictedPopupEnabled = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.IsAgeRestrictedPopupEnabled
		);
		const isAgeRestrictedItem = this.productService.product?.isAgeRestricted || false;
		const cacheAgeConfirmed = this.localStorageService.getItem(StorageKeys.ageRestrictedProductAcknowledged);
		const ageConfirmed = this.shopperService.state?.shopperScvId
			? cacheAgeConfirmed === this.shopperService.state?.shopperScvId
			: cacheAgeConfirmed === 'confirmed';
		return IsAgeRestrictedPopupEnabled && isAgeRestrictedItem && !ageConfirmed;
	}
}
